"use client";

import { useMemo } from "react";
import { ExternalLink, Puzzle } from "lucide-react";
import { Button } from "./button";
import { useTranslations } from "@/components/i18n/use-translations";
import { webioc } from "@/data/discoverability";
import { WEBIOC_NOTICE_STORAGE_KEY } from "./onboarding-storage";
import { ModalOverlay } from "./modal-overlay";
import { OnboardingNoticeCard } from "./onboarding-notice-card";

type WebIocNoticeProps = {
    onDismiss: () => void;
};

const primaryButtonClassName =
    "bg-linear-to-r from-cyan-500 to-blue-600 text-white font-semibold shadow-lg shadow-cyan-500/30 hover:from-cyan-400 hover:to-blue-500 hover:text-white rounded-full";

export function WebIocNotice({ onDismiss }: WebIocNoticeProps) {
    const { messages } = useTranslations();
    const titleLines = useMemo(() => messages.webiocNotice.title.split("\n"), [messages.webiocNotice.title]);

    const handleDismiss = () => {
        if (typeof window !== "undefined") {
            window.localStorage.setItem(WEBIOC_NOTICE_STORAGE_KEY, "acknowledged");
        }
        onDismiss();
    };

    return (
        <ModalOverlay>
            <OnboardingNoticeCard
                badge={messages.webiocNotice.badge}
                titleLines={titleLines}
                description={messages.webiocNotice.description}
                bullets={[...messages.webiocNotice.bullets]}
                icon={<Puzzle className="h-5 w-5" strokeWidth={1.75} />}
                secondaryLabel={messages.webiocNotice.maybeLater}
                dismissLabel={messages.webiocNotice.dismissLabel}
                onDismiss={handleDismiss}
                primaryAction={
                    <Button asChild className={primaryButtonClassName}>
                        <a href={webioc.chromeWebStoreUrl} target="_blank" rel="noopener noreferrer" onClick={handleDismiss}>
                            {messages.webiocNotice.chromeStoreCta}
                            <ExternalLink className="h-4 w-4" />
                        </a>
                    </Button>
                }
            />
        </ModalOverlay>
    );
}
